import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import * as Lucide from 'lucide-react';
import { SelectOption } from '../types';
import { AppDialogFrame } from '../../../components/overlays/AppDialogFrame';
import { EngineeringSupportBanner } from '../../../components/overlays/EngineeringSupportBanner';

// --- Card ---
export const Card: React.FC<React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }> = ({ children, className = '', ...props }) => (
  <div className={`bg-white shadow-sm rounded-lg border border-border p-6 ${className}`} {...props}>
    {children}
  </div>
);

// --- Typography ---
export const SectionHeader: React.FC<{ title: string; description: string; action?: React.ReactNode }> = ({ title, description, action }) => (
  <div className="flex justify-between items-start mb-6">
    <div>
      <h3 className="text-lg font-semibold leading-6 text-irongrey">{title}</h3>
      <p className="text-sm text-text-secondary mt-1">{description}</p>
    </div>
    {action}
  </div>
);

// --- Form Elements ---
export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label?: string; subLabel?: string }> = ({ label, subLabel, className = '', ...props }) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-irongrey mb-1">{label}</label>}
    <input
      className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 px-3 transition-colors border"
      {...props}
    />
    {subLabel && <p className="mt-1 text-xs text-text-secondary">{subLabel}</p>}
  </div>
);

export const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'outline' | 'ghost' }> = ({ children, variant = 'outline', className = '', ...props }) => {
  const baseStyles = "inline-flex items-center px-4 py-2 text-xs font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary uppercase tracking-wider whitespace-nowrap";
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-hover shadow-sm border border-transparent",
    outline: "border border-primary text-primary bg-white hover:bg-[#EAF8F4] shadow-sm",
    ghost: "text-text-secondary hover:text-irongrey bg-transparent"
  };

  return (
    <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

// --- Custom Select ---
interface CustomSelectProps {
  label?: string;
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  testId?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({ label, options, value, onChange, className = '', testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const listboxIdSeed = useId();
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const selectedOption = options.find(o => o.value === value) || options[0];
  const listboxId = `custom-select-listbox-${listboxIdSeed.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const menuTestId = testId ? `${testId}-menu` : undefined;
  const toOptionTestId = (optionValue: string): string | undefined => {
    if (!testId) return undefined;
    const normalized = String(optionValue)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${testId}-option-${normalized || 'option'}`;
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      setIsOpen(false);
    }

    function handleScroll() {
       if (isOpen) updateCoords();
    }

    if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleScroll);
        updateCoords();
    }

    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen]);

  const updateCoords = () => {
      if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setCoords({
              top: rect.bottom,
              left: rect.left,
              width: rect.width
          });
      }
  };

  const toggleOpen = () => {
      if (!isOpen) {
        updateCoords();
      }
      setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && <label className="block text-sm font-medium text-irongrey mb-1">{label}</label>}
      <button
        ref={buttonRef}
        data-testid={testId}
        type="button"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        className="relative w-full bg-white border border-border rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm h-10 transition-colors"
      >
        <span className="block truncate">{selectedOption?.label}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <Icon name="ChevronDown" className="text-text-tertiary w-5 h-5" />
        </span>
      </button>

      {isOpen && createPortal(
        <div
            id={listboxId}
            ref={portalRef}
            role="listbox"
            data-testid={menuTestId}
            className="fixed z-[9999] bg-white shadow-xl max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm border border-border"
            style={{
                top: coords.top + 4,
                left: coords.left,
                width: coords.width
            }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              data-testid={toOptionTestId(option.value)}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-muted ${option.value === value ? 'text-primary font-medium bg-[#EAF8F4]' : 'text-irongrey'}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <span className="block truncate">{option.label}</span>
              {option.value === value && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary">
                  <Icon name="Check" className="w-4 h-4" />
                </span>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// --- MultiSelect Combobox ---
interface MultiSelectProps {
  label?: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string) => void;
  onRemove: (value: string) => void;
  className?: string;
  placeholder?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ label, options, value, onChange, onRemove, className = '', placeholder = 'Select...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const updateCoords = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
         setIsOpen(false);
         setSearchTerm('');
      }
    }

    function handleScrollResize() {
       if (isOpen) updateCoords();
    }

    if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScrollResize, true);
        window.addEventListener("resize", handleScrollResize);
        updateCoords();
    }

    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScrollResize, true);
        window.removeEventListener("resize", handleScrollResize);
    };
  }, [isOpen]);

  const handleContainerClick = () => {
    if (!isOpen) {
        setIsOpen(true);
        updateCoords();
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && searchTerm === '' && value.length > 0) {
        onRemove(value[value.length - 1]);
    }
    if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
    }
  };

  const filteredOptions = options.filter(o =>
      !value.includes(o.value) &&
      o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && <label className="block text-sm font-medium text-irongrey mb-1">{label}</label>}
      <div
        ref={triggerRef}
        onClick={handleContainerClick}
        className="relative w-full bg-white border border-border rounded-md shadow-sm px-2 py-1 cursor-text focus-within:ring-1 focus-within:ring-primary focus-within:border-primary sm:text-sm min-h-[38px] flex flex-wrap gap-1.5 items-center transition-colors"
      >
        {value.map(val => {
           const optionLabel = options.find(o => o.value === val)?.label || val;
           return (
             <span key={val} className="bg-muted px-2 py-0.5 rounded text-xs flex items-center font-medium border border-border text-irongrey">
               {optionLabel}
               <button
                 type="button"
                 onClick={(e) => {
                   e.stopPropagation();
                   onRemove(val);
                 }}
                 className="ml-1.5 text-text-tertiary hover:text-strawberry focus:outline-none flex items-center justify-center"
               >
                 <Icon name="X" className="w-3 h-3" strokeWidth={3} />
               </button>
             </span>
           );
        })}

        <input
            ref={inputRef}
            type="text"
            className="flex-1 min-w-[4rem] border-none outline-none focus:ring-0 p-0 text-sm bg-transparent placeholder-text-tertiary h-6"
            placeholder={value.length === 0 ? placeholder : ''}
            value={searchTerm}
            onChange={e => {
                setSearchTerm(e.target.value);
                if (!isOpen) setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <Icon name="ChevronDown" className="text-text-tertiary w-4 h-4" />
        </div>
      </div>

      {isOpen && createPortal(
        <div
            ref={dropdownRef}
            className="fixed z-[9999] bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm border border-border"
            style={{
                top: coords.top + 4,
                left: coords.left,
                width: coords.width
            }}
        >
          {filteredOptions.length > 0 ? (
             filteredOptions.map((option) => (
            <div
              key={option.value}
              className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-muted text-irongrey transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onChange(option.value);
                setSearchTerm('');
                inputRef.current?.focus();
              }}
            >
              <span className="block truncate">{option.label}</span>
            </div>
          ))
          ) : (
            <div className="py-2 px-3 text-text-secondary italic text-xs">No options found</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// --- Toggle Switch ---
export const Toggle: React.FC<{ checked: boolean; onChange: () => void; label?: string; requiredText?: boolean }> = ({ checked, onChange, label, requiredText = true }) => (
  <div className="flex items-center justify-between py-4">
    <span className="text-sm font-medium text-irongrey">{label}</span>
    <div className="flex items-center gap-3">
      {requiredText && <span className="text-xs text-text-secondary uppercase">Required</span>}
      <button
        type="button"
        className={`${checked ? 'bg-primary' : 'bg-border'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onChange}
      >
        <span
          aria-hidden="true"
          className={`${checked ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </button>
    </div>
  </div>
);

// --- Checkbox ---
export const Checkbox: React.FC<{ checked?: boolean; onChange?: () => void; className?: string }> = ({ checked, onChange, className = '' }) => (
  <div
    onClick={onChange}
    className={`w-5 h-5 rounded border-border cursor-pointer flex items-center justify-center transition-all ${checked ? 'bg-aquamarine border-aquamarine' : 'bg-white border-2'} ${className}`}
  >
    {checked && <Icon name="Check" className="text-white w-3 h-3" strokeWidth={4} />}
  </div>
);

// --- Icon Wrapper ---
export const Icon: React.FC<{ name: string; className?: string; strokeWidth?: number }> = ({ name, className = '', strokeWidth = 2 }) => {
  const LucideIcon = (Lucide as any)[name];
  if (!LucideIcon) return null;
  return <LucideIcon className={className} strokeWidth={strokeWidth} />;
};

// --- Modal ---
export const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; testId?: string }> = ({ isOpen, onClose, title, children, testId }) => {
  return (
    <AppDialogFrame
      open={isOpen}
      onClose={onClose}
      title={title}
      testId={testId}
      maxWidthClassName="max-w-md"
      panelClassName="rounded-lg"
      bodyPaddingClassName="p-4"
      titleClassName="text-lg font-semibold text-irongrey"
    >
      {children}
    </AppDialogFrame>
  );
};

export type ToastVariant = 'error' | 'success' | 'warning' | 'info';

type ToastProps = {
  message: string;
  variant?: ToastVariant;
  visible: boolean;
  onDismiss: () => void;
  durationMs?: number;
  title?: string;
};

// --- Textarea ---
interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: string;
  /** Shown below the textarea. When maxLength is also set, a character counter is appended on the right. */
  subLabel?: string;
  className?: string;
}

export const Textarea: React.FC<TextareaProps> = ({
  label,
  subLabel,
  className = '',
  value,
  maxLength,
  ...props
}) => {
  const charCount = typeof value === 'string' ? value.length : 0;
  const showCounter = maxLength != null;
  const atLimit = showCounter && charCount >= (maxLength ?? Infinity);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-irongrey mb-1">{label}</label>
      )}
      <textarea
        className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm px-3 py-2 transition-colors border resize-y min-h-[80px]"
        value={value}
        maxLength={maxLength}
        {...props}
      />
      {(subLabel != null || showCounter) && (
        <div className="mt-1 flex items-center justify-between gap-2">
          {subLabel != null ? (
            <p className="text-xs text-text-secondary">{subLabel}</p>
          ) : (
            <span />
          )}
          {showCounter && (
            <p className={`text-xs tabular-nums shrink-0 ${atLimit ? 'text-strawberry font-medium' : 'text-text-tertiary'}`}>
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// --- Pagination Bar ---
interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  className?: string;
  prevTestId?: string;
  nextTestId?: string;
}

export const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  className = '',
  prevTestId,
  nextTestId,
}) => (
  <div className={`flex items-center justify-end gap-2 ${className}`}>
    <button
      type="button"
      data-testid={prevTestId}
      onClick={onPrev}
      disabled={currentPage <= 1}
      aria-label="Previous page"
      className="inline-flex items-center justify-center h-8 min-w-[32px] px-2 rounded border border-border text-text-secondary hover:text-irongrey hover:border-primary/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
    >
      <Icon name="ChevronLeft" className="w-4 h-4" />
    </button>
    <span className="text-xs text-text-secondary tabular-nums select-none whitespace-nowrap">
      Page {currentPage} of {totalPages}
    </span>
    <button
      type="button"
      data-testid={nextTestId}
      onClick={onNext}
      disabled={currentPage >= totalPages}
      aria-label="Next page"
      className="inline-flex items-center justify-center h-8 min-w-[32px] px-2 rounded border border-border text-text-secondary hover:text-irongrey hover:border-primary/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
    >
      <Icon name="ChevronRight" className="w-4 h-4" />
    </button>
  </div>
);

export const Toast: React.FC<ToastProps> = ({
  message,
  variant = 'error',
  visible,
  onDismiss,
  durationMs = 5000,
  title,
}) => {
  useEffect(() => {
    if (!visible) return undefined;
    const timeout = window.setTimeout(() => {
      onDismiss();
    }, durationMs);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [durationMs, onDismiss, visible, message, variant]);

  if (!visible || !message || typeof document === 'undefined') return null;

  const styles: Record<ToastVariant, string> = {
    error: 'bg-error-background border border-strawberry/20 text-strawberry',
    success: 'bg-success-background border border-jungle/20 text-jungle',
    warning: 'bg-warning-background border border-coral/20 text-irongrey',
    info: 'bg-info-background border border-primary/20 text-irongrey',
  };

  const iconByVariant: Record<ToastVariant, string> = {
    error: 'AlertCircle',
    success: 'CheckCircle',
    warning: 'AlertTriangle',
    info: 'Info',
  };

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[10001] w-full max-w-sm pointer-events-none">
      <div className={`pointer-events-auto rounded-lg shadow-lg p-4 animate-in slide-in-from-bottom-2 fade-in duration-300 ${styles[variant]}`}>
        {variant === 'error' ? <EngineeringSupportBanner compact className="mb-3" /> : null}
        <div className="flex items-start gap-3">
          <Icon name={iconByVariant[variant]} className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            {title ? <p className="text-sm font-semibold mb-0.5">{title}</p> : null}
            <p className="text-sm leading-5">{message}</p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Dismiss notification"
          >
            <Icon name="X" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
